/**
 * The resource requirement and the combinators (CAPABILITIES.md §3.3). A policy's list is already
 * "all of", so there is no `allOf`.
 */
import type { Facts, ReasonCode, Requirement } from "../types";
import { requirement } from "./requirement";

/** a test of the thing acted on (an order, a line), typed by the capability's resource */
export const resource = <R>(predicate: (resource: R) => boolean, reason: ReasonCode, id = `resource:${reason}`): Requirement<R> =>
  requirement<R>(id, reason, (_facts, r) => predicate(r));

/** one of them passes; its reason is the caller's, since no single failure explains it */
export const anyOf = <R>(reason: ReasonCode, ...reqs: Requirement<R>[]): Requirement<R> =>
  requirement<R>(`anyOf(${reqs.map((r) => r.id).join(",")})`, reason, (facts, r) => reqs.some((q) => q.test(facts, r)));

export const not = <R>(req: Requirement<R>, reason: ReasonCode): Requirement<R> =>
  requirement<R>(`not(${req.id})`, reason, (facts, r) => !req.test(facts, r));

type Condition<R> = Requirement<R> | ((facts: Facts, resource: R) => boolean);

/**
 * The escalation pattern: `req` applies only while `condition` holds, e.g.
 * `when(billPrinted, permission("EDIT_ORDER_AFTER_BILL_PRINT"))`. Its reason and remedy are `req`'s.
 */
export function when<R>(condition: Condition<R>, req: Requirement<R>): Requirement<R> {
  const holds = typeof condition === "function" ? condition : (f: Facts, r: R) => condition.test(f, r);
  const name = typeof condition === "function" ? condition.name || "condition" : condition.id;
  return requirement<R>(`when(${name}):${req.id}`, req.reason, (facts, r) => !holds(facts, r) || req.test(facts, r), req.remedy);
}
